import {version as appVersion} from '@/../package.json';
import {SupportedLocale} from '@/initializers/i18next';
import {
  Document,
  DocumentAttachmentUploadUrl,
  DocumentAttachmentId,
  DocumentAttachmentType,
  DocumentForm,
  DocumentFormPayload,
  FullDocument,
  FullDocumentReadyForSignature,
  DocumentAttachment,
  PartialDocumentReadyForResubmittingSignature,
} from '@/interfaces/document';
import {Endpoints, API} from '../api';

const buildSignatureRequest = ({
  document,
  documentLocale,
  modelJsonVersion,
  signatureQuestion,
  signatureEmailSubject,
}: {
  document: FullDocumentReadyForSignature;
  documentLocale: SupportedLocale;
  modelJsonVersion: string;
  signatureQuestion: string;
  signatureEmailSubject: string;
}) => {
  return {
    transactionInfo: {
      appVersion,
      productCategory: 'SAVINGS',
      application: 'BMW',
      jsonVersion: modelJsonVersion,
      data: document,
    },
    requestLanguage: documentLocale,
    signerQuestion: signatureQuestion,
    subject: signatureEmailSubject,
  };
};

const buildResubmitForSignatureRequest = ({
  serializedDocument,
  document,
}: {
  serializedDocument: PartialDocumentReadyForResubmittingSignature;
  document: FullDocument;
}) => {
  return {
    sectionEE: document.sectionEE,
    dataEE: serializedDocument.EE,
  };
};

export interface LatestTransaction {
  clients: ContractHolder[];
  documentId: string;
}

export enum ClientType {
  CONTRACT_HOLDER = 'ContractHolder',
  SPOUSE = 'Spouse',
}
export interface ContractHolder {
  firstName: string;
  lastName: string;
  type: ClientType;
}

export interface DocumentListFlags {
  incomplete: boolean;
  pending: boolean;
  sent: boolean;
}

export type AllDocuments = {
  complete: Document[];
  incomplete: Document[];
  submitted: Document[];
  latestTransaction: LatestTransaction;
  waitingSignature: Document[];
  waitingSubmit: Document[];
  flags: DocumentListFlags;
};

export function getAll() {
  return API.get<AllDocuments>(Endpoints.documents.collection);
}

export function getById(documentId: string) {
  return API.get<FullDocument>(Endpoints.documents.single, {params: {documentId}});
}

export function create(
  newDocument: Pick<FullDocument, 'type' | 'name' | 'modelVersion' | 'sectionINVE' | 'advisorCode'> &
    Partial<Pick<FullDocument, 'sectionCOMP'>>,
) {
  return API.post<Document>(Endpoints.documents.collection, newDocument);
}

export function clone(documentId: string, clientType: ClientType, advisorCode: string) {
  return API.post<Document>(
    Endpoints.documents.clone,
    {clientType: clientType, advisorCode: advisorCode},
    {params: {documentId}},
  );
}

export function update(documentId: string, updatedDocument: Partial<FullDocument>) {
  return API.patch<FullDocument>(Endpoints.documents.single, updatedDocument, {params: {documentId}});
}

export function remove(documentId: string) {
  return API.delete(Endpoints.documents.single, {params: {documentId}});
}

export function submitForSignature({
  documentId,
  document,
  documentLocale,
  modelJsonVersion,
  signatureQuestion,
  signatureEmailSubject,
}: {
  documentId: string;
  document: FullDocumentReadyForSignature;
  documentLocale: SupportedLocale;
  modelJsonVersion: string;
  signatureQuestion: string;
  signatureEmailSubject: string;
}) {
  return API.post(
    Endpoints.documents.submitForSignature,
    buildSignatureRequest({document, documentLocale, modelJsonVersion, signatureQuestion, signatureEmailSubject}),
    {
      params: {documentId},
      timeout: 5 * 60 * 1000, // 5 minutes
    },
  );
}

export function resubmitForSignature({
  documentId,
  serializedDocument,
  document,
}: {
  documentId: string;
  serializedDocument: PartialDocumentReadyForResubmittingSignature;
  document: FullDocument;
}) {
  return API.put(
    Endpoints.documents.submitForSignature,
    buildResubmitForSignatureRequest({
      serializedDocument,
      document,
    }),
    {
      params: {documentId},
    },
  );
}

export function retryFinalSubmit(documentId: string) {
  return API.post(Endpoints.documents.retryFinalSubmit, {}, {params: {documentId}});
}

export function retrySubmitForSignature(documentId: string) {
  return API.post(Endpoints.documents.retrySubmitForSignature, {}, {params: {documentId}});
}

export function cancelSignatureRequest(documentId: string) {
  return API.delete(Endpoints.documents.cancelSignatureRequest, {params: {documentId}});
}

export function removeSentDocumentsHistory() {
  return API.delete(Endpoints.documents.deleteSentDocuments);
}

export function getAttachments(documentId: string) {
  return API.get(Endpoints.documents.attachments.collection, {params: {documentId}});
}

export function createDocumentAttachmentUploadUrl(
  documentId: string,
  newAttachment: {
    fieldId: DocumentAttachmentId;
    fileName: string;
    type: DocumentAttachmentType;
    extension: string;
  },
) {
  return API.post<DocumentAttachmentUploadUrl>(Endpoints.documents.attachments.collection, newAttachment, {
    params: {documentId},
  });
}

export function downloadAttachment(documentId: string, attachmentId: string) {
  return API.get<DocumentAttachment>(Endpoints.documents.attachments.single, {params: {documentId, attachmentId}});
}

export function removeDocumentAttachment(documentId: string, attachmentId: string) {
  return API.delete(Endpoints.documents.attachments.single, {params: {documentId, attachmentId}});
}

export function getDocumentAttachments(documentId: string) {
  return API.get(Endpoints.documents.attachments.collection, {params: {documentId}});
}

export function generateForm(documentId: string, payload: DocumentFormPayload) {
  return API.post<DocumentForm>(Endpoints.documents.forms.generate, payload, {params: {documentId}});
}

export function getForm(documentId: string, formId: string) {
  return API.get<DocumentForm>(Endpoints.documents.forms.single, {params: {documentId, formId}});
}

export function removeForm(documentId: string, formId: string) {
  return API.delete(Endpoints.documents.forms.single, {params: {documentId, formId}});
}
